"use client";

import { Check, Copy, Download, FileText, Upload } from "lucide-react";
import { useRef, useState } from "react";
import Link from "@/components/locale-link";
import { renderSlots } from "@/components/tools/template-slots";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import {
  extractDocxText,
  extractReferences,
  type InTextCitation,
  type ReferenceExtraction,
} from "@/lib/document-extract";

const SAMPLE = `Introduction

Sleep loss impairs recall (Smith, 2020). Jones et al. (2019) found similar effects, and later work confirmed it (Smith & Doe, 2021; Brown, 2018).

References

Smith, J. (2020). Sleep and memory. Journal of Sleep, 12(3), 45-60. https://doi.org/10.1000/sleep.2020
Jones, A., Lee, B., & Park, C. (2019). Recall under stress. Mind Press.
Brown, K. (2018). Rest and cognition. Academic Press.
Taylor, R. (2017). The forgotten study. Sleep Review, 4(1), 1-9.`;

/** English strings. A translated content file overrides these through `ui`. */
export const REFERENCE_EXTRACTOR_STRINGS = {
  copied: "Copied",
  copyFailed: "Copy failed",
  readingFile: "Reading file…",
  uploadButton: "Upload .docx or .txt",
  textareaAriaLabel: "Paper text to extract references from",
  placeholder: "…or paste your full paper (or just its reference list) here",
  trySample: "Try a sample",
  clear: "Clear",
  results: "Results",
  referencesStat: "References",
  inTextCitationsStat: "In-text citations",
  doisStat: "DOIs",
  crossCheckFlagsStat: "Cross-check flags",
  noEntries:
    "No reference list was detected. Make sure your document has a heading like {references}, {bibliography}, or {worksCited} above the list — or paste just the reference list itself.",
  referencesWord: "References",
  bibliographyWord: "Bibliography",
  worksCitedWord: "Works Cited",
  extractedReferences: "Extracted references",
  copyAll: "Copy all",
  downloadTxt: "Download .txt",
  nextStep: "Next step: {link} to catch formatting issues.",
  citationCheckerLink: "run these through the APA Citation Checker",
  citationCrossCheck: "Citation cross-check",
  missingFromListHeading:
    "Cited in the text but missing from the reference list",
  neverCitedHeading: "In the reference list but never cited in the text",
  crossCheckNote:
    "The cross-check matches first-author surname + year for author–date styles (APA, Harvard). It is advisory — verify before deleting anything.",
  doisFound: "DOIs found",
  copyDois: "Copy DOIs",
  pasteDois:
    "Paste the copied DOIs into the {link} (one per line) to auto-cite every source in your chosen style.",
  citationGeneratorLink: "citation generator",
  couldNotReadFile: "Could not read the file.",
} as const;

type Strings = typeof REFERENCE_EXTRACTOR_STRINGS;

function CopyButton({
  text,
  label,
  strings,
}: {
  text: string;
  label: string;
  strings: Strings;
}) {
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState(false);

  async function handleCopy() {
    const ok = await writeToClipboard(text);
    if (ok) {
      setCopied(true);
      setFailed(false);
      setTimeout(() => setCopied(false), 1500);
    } else {
      setFailed(true);
    }
  }

  function buttonLabel(): string {
    if (copied) {
      return strings.copied;
    }
    if (failed) {
      return strings.copyFailed;
    }
    return label;
  }

  return (
    <button
      className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1 font-medium text-foreground text-xs hover:bg-muted"
      onClick={handleCopy}
      type="button"
    >
      {copied ? (
        <Check aria-hidden="true" className="size-3.5" />
      ) : (
        <Copy aria-hidden="true" className="size-3.5" />
      )}
      {buttonLabel()}
    </button>
  );
}

function downloadText(filename: string, content: string) {
  const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-xs">{label}</p>
    </div>
  );
}

function EntriesSection({
  entries,
  strings,
}: {
  entries: string[];
  strings: Strings;
}) {
  if (entries.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-card p-5">
        <p className="text-muted-foreground text-sm leading-relaxed">
          {renderSlots(strings.noEntries, {
            references: <strong>{strings.referencesWord}</strong>,
            bibliography: <strong>{strings.bibliographyWord}</strong>,
            worksCited: <strong>{strings.worksCitedWord}</strong>,
          })}
        </p>
      </div>
    );
  }
  const joined = entries.join("\n");
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-medium text-foreground text-sm">
          {strings.extractedReferences}
        </h3>
        <div className="flex flex-wrap gap-2">
          <CopyButton label={strings.copyAll} strings={strings} text={joined} />
          <button
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1 font-medium text-foreground text-xs hover:bg-muted"
            onClick={() => downloadText("references.txt", joined)}
            type="button"
          >
            <Download aria-hidden="true" className="size-3.5" />
            {strings.downloadTxt}
          </button>
        </div>
      </div>
      <ol className="space-y-2">
        {entries.map((entry, index) => (
          <li
            className="rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed"
            key={`${index}-${entry.slice(0, 40)}`}
          >
            {entry}
          </li>
        ))}
      </ol>
      <p className="text-muted-foreground text-sm">
        {renderSlots(strings.nextStep, {
          link: (
            <Link
              className="text-primary underline-offset-4 hover:underline"
              href="/tools/citation-checker"
            >
              {strings.citationCheckerLink}
            </Link>
          ),
        })}
      </p>
    </div>
  );
}

function CrossCheckSection({
  result,
  strings,
}: {
  result: ReferenceExtraction;
  strings: Strings;
}) {
  if (result.missingFromList.length === 0 && result.neverCited.length === 0) {
    return null;
  }
  return (
    <div className="space-y-3">
      <h3 className="font-medium text-foreground text-sm">
        {strings.citationCrossCheck}
      </h3>
      {result.missingFromList.length > 0 ? (
        <div className="rounded-lg border border-border border-l-4 border-l-amber-400 bg-card p-4">
          <p className="font-medium text-foreground text-sm">
            {strings.missingFromListHeading}
          </p>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground text-sm">
            {result.missingFromList.map((citation: InTextCitation) => (
              <li key={`${citation.surname}-${citation.year}`}>
                {citation.raw}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {result.neverCited.length > 0 ? (
        <div className="rounded-lg border border-border border-l-4 border-l-sky-400 bg-card p-4">
          <p className="font-medium text-foreground text-sm">
            {strings.neverCitedHeading}
          </p>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground text-sm">
            {result.neverCited.map((entry) => (
              <li key={entry.slice(0, 60)}>{entry}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <p className="text-muted-foreground text-xs leading-relaxed">
        {strings.crossCheckNote}
      </p>
    </div>
  );
}

function DoiSection({ dois, strings }: { dois: string[]; strings: Strings }) {
  if (dois.length === 0) {
    return null;
  }
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-medium text-foreground text-sm">
          {strings.doisFound}
        </h3>
        <CopyButton
          label={strings.copyDois}
          strings={strings}
          text={dois.join("\n")}
        />
      </div>
      <div className="rounded-lg border border-border bg-card p-4">
        <ul className="space-y-1 text-muted-foreground text-sm">
          {dois.map((doi) => (
            <li key={doi}>{doi}</li>
          ))}
        </ul>
        <p className="mt-3 text-muted-foreground text-xs leading-relaxed">
          {renderSlots(strings.pasteDois, {
            link: (
              <Link
                className="text-primary underline-offset-4 hover:underline"
                href="/"
              >
                {strings.citationGeneratorLink}
              </Link>
            ),
          })}
        </p>
      </div>
    </div>
  );
}

function ResultsSection({
  result,
  strings,
}: {
  result: ReferenceExtraction;
  strings: Strings;
}) {
  return (
    <section aria-live="polite" className="space-y-5">
      <h2 className="font-semibold text-foreground text-xl">
        {strings.results}
      </h2>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard
          label={strings.referencesStat}
          value={result.entries.length}
        />
        <StatCard
          label={strings.inTextCitationsStat}
          value={result.inTextCitations.length}
        />
        <StatCard label={strings.doisStat} value={result.dois.length} />
        <StatCard
          label={strings.crossCheckFlagsStat}
          value={result.missingFromList.length + result.neverCited.length}
        />
      </div>
      <EntriesSection entries={result.entries} strings={strings} />
      <CrossCheckSection result={result} strings={strings} />
      <DoiSection dois={result.dois} strings={strings} />
    </section>
  );
}

export default function ReferenceExtractorWidget() {
  const strings = useToolUi(REFERENCE_EXTRACTOR_STRINGS);
  const [text, setText] = useState("");
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const hasInput = text.trim().length > 0;
  const result = hasInput ? extractReferences(text) : null;

  async function handleFile(file: File) {
    setBusy(true);
    setFileError(null);
    try {
      let extracted: string;
      if (file.name.toLowerCase().endsWith(".docx")) {
        extracted = await extractDocxText(await file.arrayBuffer());
      } else {
        extracted = await file.text();
      }
      setText(extracted);
      setFileName(file.name);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : strings.couldNotReadFile;
      setFileError(message);
      setFileName(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {/* Input */}
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <button
            className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-3 py-1.5 font-medium text-foreground text-sm hover:bg-muted"
            disabled={busy}
            onClick={() => fileInputRef.current?.click()}
            type="button"
          >
            <Upload aria-hidden="true" className="size-4" />
            {busy ? strings.readingFile : strings.uploadButton}
          </button>
          <input
            accept=".docx,.txt,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) {
                handleFile(file);
              }
              event.target.value = "";
            }}
            ref={fileInputRef}
            type="file"
          />
          {fileName ? (
            <span className="inline-flex items-center gap-1.5 text-muted-foreground text-sm">
              <FileText aria-hidden="true" className="size-4" />
              {fileName}
            </span>
          ) : null}
        </div>
        {fileError ? (
          <p className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-destructive text-sm">
            {fileError}
          </p>
        ) : null}
        <textarea
          aria-label={strings.textareaAriaLabel}
          className="min-h-[220px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          onChange={(event) => {
            setText(event.target.value);
            setFileName(null);
          }}
          placeholder={strings.placeholder}
          value={text}
        />
        <div className="flex flex-wrap gap-3">
          <button
            className="rounded-md border border-border bg-background px-3 py-1.5 font-medium text-foreground text-sm hover:bg-muted"
            onClick={() => {
              setText(SAMPLE);
              setFileName(null);
              setFileError(null);
            }}
            type="button"
          >
            {strings.trySample}
          </button>
          {hasInput ? (
            <button
              className="rounded-md border border-border bg-background px-3 py-1.5 font-medium text-foreground text-sm hover:bg-muted"
              onClick={() => {
                setText("");
                setFileName(null);
                setFileError(null);
              }}
              type="button"
            >
              {strings.clear}
            </button>
          ) : null}
        </div>
      </div>

      {/* Results */}
      {result ? <ResultsSection result={result} strings={strings} /> : null}
    </>
  );
}
